import React, { ReactNode } from 'react'
import { Collapse, LinearProgress, Stack } from '@mui/material'
import { EmptyPlaceholder } from '../EmptyPlaceholder/EmptyPlaceholder'
import LoadingBackdrop from '../LoadingBackdrop/LoadingBackdrop'

type DataTableSectionProps = {
    isLoading: boolean
    isError: boolean
    entityLabel: string
    loadingTitle: string
    children: ReactNode
}

const ERROR_SUBTITLE = 'Refresh the page or try again in a moment.'

export const DataTableSection = ({
    isLoading,
    isError,
    entityLabel,
    loadingTitle,
    children,
}: DataTableSectionProps) => {
    if (isLoading) {
        return <LoadingBackdrop loading title={loadingTitle} subtitle={`Loading ${entityLabel}`} />
    }

    if (isError) {
        return (
            <EmptyPlaceholder
                title={`Unable to load ${entityLabel}`}
                subtitle={ERROR_SUBTITLE}
                sx={{ minHeight: 280 }}
            />
        )
    }

    return <>{children}</>
}

type DataTableNestedPanelProps = {
    isLoading?: boolean
    isError?: boolean
    errorText?: string
    /** Reads the panel as inert without blocking it -- searching and sorting still work. */
    isDimmed?: boolean
    children: ReactNode
}

export const DataTableNestedPanel = ({
    isLoading = false,
    isError = false,
    errorText = '',
    isDimmed = false,
    children,
}: DataTableNestedPanelProps) => {
    if (isLoading) {
        return (
            <Stack color="figma.primary">
                <LinearProgress color="inherit" />
            </Stack>
        )
    }

    return (
        <Collapse in appear timeout="auto">
            <Stack pl={{ xs: 1.5, md: 3 }} pr={{ xs: 1, md: 2 }} pb={2} sx={{ opacity: isDimmed ? 0.6 : 1 }}>
                {isError ? <EmptyPlaceholder text={errorText} sx={{ minHeight: 96 }} /> : children}
            </Stack>
        </Collapse>
    )
}
